import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'

const navLinks = [
  { href: '/', label: 'Home' },
  { href: 'https://compare.bollettaamica.com/home/energia', label: 'Luce e Gas', external: true },
  { href: 'https://compare.bollettaamica.com/home/fibra', label: 'Fibra', external: true },
  { href: 'https://compare.bollettaamica.com/home/streaming', label: 'Streaming', external: true },
  { href: '/il-blog-di-carletto', label: 'Il blog di Carletto' },
  { href: '/lavora-con-noi', label: 'Lavora con noi' },
]

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location])

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'py-3' : 'py-4'
      }`}
    >
      <div className="container-custom">
        <nav className={`flex items-center justify-between px-6 py-3 rounded-2xl transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/70 backdrop-blur-2xl shadow-lg shadow-black/5 border border-white/50' 
            : 'bg-white/50 backdrop-blur-xl border border-white/30'
        }`}>
          {/* Logo */}
          <Link to="/" className="relative z-10">
            <img
              src={isScrolled ? '/logo_dark.svg' : '/logo_dark.svg'}
              alt="Bolletta Amica"
              className="h-7 md:h-8 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-dark-600 font-medium hover:text-dark-900 hover:bg-dark-100/50 rounded-xl transition-all duration-200"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 font-medium rounded-xl transition-all duration-200 ${
                    location.pathname === link.href
                      ? 'text-primary bg-primary/10'
                      : 'text-dark-600 hover:text-dark-900 hover:bg-dark-100/50'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <a
              href="https://compare.bollettaamica.com/home/energia"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm shadow-lg shadow-primary/25"
            >
              Confronta Ora
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden relative z-10 p-2.5 hover:bg-dark-100/50 rounded-xl transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-dark-800" />
            ) : (
              <Menu className="w-6 h-6 text-dark-800" />
            )}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="lg:hidden absolute top-full left-4 right-4 mt-2 bg-white/80 backdrop-blur-2xl rounded-2xl shadow-xl shadow-black/10 border border-white/50 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) =>
                  link.external ? (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dark-700 font-medium py-2 hover:text-primary transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={`font-medium py-2 transition-colors ${
                        location.pathname === link.href
                          ? 'text-primary'
                          : 'text-dark-700 hover:text-primary'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )
                )}
                <div className="pt-4 border-t">
                  <a
                    href="https://compare.bollettaamica.com/home/energia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full text-center"
                  >
                    Confronta Ora
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
